import { defineStore } from 'pinia';

import { getSights, type GetSightsData } from '@/shared/api/experience';
import type { ArticleInList } from '@/shared/models/articleInList';
import { normalizeArticleInList } from '@/shared/transforms/articleInList';
import { useLogger } from '@/shared/utils/logger';

export type GetSightsParams = GetSightsData['query'];

export const useSightEntity = defineStore('journal:sight', {
  state: () => ({
    /**
     * Список достопримечательностей.
     */
    sightsState: [] as ArticleInList[],

    /**
     * Текущая страница.
     */
    currentPage: 1,

    /**
     * Общее количество достопримечательностей.
     */
    totalSightsCount: 0,

    /**
     * Признак наличия незагруженных достопримечательностей.
     */
    hasNextSights: false,

    /**
     * Флаг индикации загрузки.
     */
    isLoading: false,

    /**
     * Флаг, показывающий, что загрузка окончилать неудачей.
     */
    isLoadingFailed: false,
  }),

  getters: {
    /**
     * Список достопримечательностей.
     */
    sights: (state) => state.sightsState,

    /**
     * Текущая страница.
     */
    page: (state) => state.currentPage,

    /**
     * Общее количество достопримечательностей.
     */
    totalCount: (state) => state.totalSightsCount,

    /**
     * Признак наличия незагруженных достопримечательностей.
     */
    hasNext: (state) => state.hasNextSights,

    /**
     * Флаг индикации загрузки.
     */
    loading: (state) => state.isLoading,

    /**
     * Флаг, показывающий, что загрузка окончилать неудачей.
     */
    loadingFailed: (state) => state.isLoadingFailed,
  },

  actions: {
    logger: useLogger,

    /**
     * Загружает список достопримечательностей.
     *
     * @param params дополнительные параметры запроса
     */
    async getSights(params: { page?: number; pageSize: number; citySlugs: string[] }) {
      let response;

      this.isLoading = true;
      this.isLoadingFailed = false;

      const query = {
        page: params.page ?? this.currentPage,
        page_size: params.pageSize,
        city__slug: params.citySlugs?.join(','),
      };

      try {
        response = await getSights({ query });
      } catch (e) {
        this.logger().error(e);
        this.isLoading = false;
        this.isLoadingFailed = true;

        return;
      }

      const normalizedArticles = response.data?.results?.map((article) => normalizeArticleInList(article)) ?? [];

      this.sightsState = this.sights.length ? [...this.sights, ...normalizedArticles] : normalizedArticles;
      this.totalSightsCount = response.data?.count ?? 0;
      this.currentPage += 1;
      this.hasNextSights = Boolean(response.data?.next);
      this.isLoading = false;
    },

    /**
     * Сбрасывает состояние экземпляра сущности.
     */
    reset() {
      this.sightsState = [];
      this.currentPage = 1;
      this.totalSightsCount = 0;
      this.hasNextSights = false;
      this.isLoading = false;
      this.isLoadingFailed = false;
    },
  },
});
